import { type FC } from 'react';
import { Clock } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { SpecialEvent, ItineraryItem } from '@/types';

interface ItinerarySectionProps {
  event: SpecialEvent;
}

/**
 * Itinerary section with vertical timeline layout
 * Displays schedule items with time, title, description, and optional icons
 */
export const ItinerarySection: FC<ItinerarySectionProps> = ({ event }) => {
  if (!event.itinerary || event.itinerary.length === 0) {
    return null;
  }

  // Dynamically get icon component from Lucide
  const getIcon = (iconName?: string) => {
    if (!iconName) {
      return Clock;
    }
    // Try to get the icon from lucide-react
    const IconComponent = (LucideIcons as Record<string, unknown>)[iconName];
    if (IconComponent && typeof IconComponent === 'function') {
      return IconComponent as typeof Clock;
    }
    return Clock; // Fallback
  };

  return (
    <section className="py-16 bg-gradient-to-b from-turquoise-900 to-turquoise-800 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-orange-500 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-viking text-white mb-8 text-center drop-shadow-lg">
            Event Schedule
          </h2>

          {/* Vertical Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-500 via-turquoise-400 to-orange-500" />

            <div className="space-y-8">
              {event.itinerary.map((item: ItineraryItem, index: number) => {
                const Icon = getIcon(item.icon);
                const isLast = index === event.itinerary.length - 1;

                return (
                  <div key={item.id} className="relative pl-20">
                    {/* Timeline dot and icon */}
                    <div className="absolute left-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30 flex items-center justify-center ring-4 ring-turquoise-800">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                    </div>

                    {/* Content card */}
                    <div
                      className={`bg-turquoise-800/50 backdrop-blur-sm rounded-lg border-2 border-turquoise-600/50 shadow-xl p-6 hover:border-orange-500/50 transition-colors ${
                        isLast ? '' : 'mb-4'
                      }`}
                    >
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className="text-sm font-bold text-white bg-orange-500 px-3 py-1 rounded-full shadow-md">
                          {item.time}
                        </span>
                        <h3 className="text-xl font-viking text-orange-300 flex-1">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-sm text-white/80 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
